import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@/components/ui';
import api from '@/lib/api';
import type { ApiResponse } from '@/types';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('无效的验证链接');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await api.post<ApiResponse>('/auth/verify-email', { token });
        if (response.data.success) {
          setStatus('success');
          setMessage('邮箱验证成功！您现在可以登录使用完整功能。');
        }
      } catch (error: any) {
        setStatus('error');
        const errorMsg = error.response?.data?.error?.message || '验证失败，链接可能已过期';
        setMessage(errorMsg);
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'loading' && (
            <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4">
              <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          )}
          {status === 'error' && (
            <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mb-4">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          )}
          <CardTitle>
            {status === 'loading' && '验证中...'}
            {status === 'success' && '验证成功'}
            {status === 'error' && '验证失败'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">{message}</p>

          {status === 'success' && (
            <Link to="/login">
              <Button className="w-full">
                前往登录
              </Button>
            </Link>
          )}

          {status === 'error' && (
            <div className="space-y-2">
              <Link to="/login">
                <Button className="w-full">
                  前往登录
                </Button>
              </Link>
              <p className="text-sm text-muted-foreground">
                如果问题仍然存在，请联系管理员
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
